import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePrintArtwork } from './print/usePrintArtwork';
import { HIDDEN_PIN_TYPES_CHIP_STACK } from './HiddenPinTypesChip';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import type { TeamMap } from '../lib/types';
import { buildMapPrintRequest } from '../lib/mapPrintPayload';
import { safePrintUrl } from '../lib/mapPrintClient';
import { usePrintPinSizes } from './print/usePrintPinSizes';
import { useMapPrintJobs } from '../lib/useMapPrintJobs';
import { groundCenterForPrint, printPropertyBorders } from '../lib/mapPrintRegion';
import { MapPrintSection } from './MapPrintSection';
import { Icon } from './Icon';
import { PrintPreviewPanel } from './PrintPreviewPanel';
import { PrintFrameLayers } from './print/PrintFrameLayers';
import { MapProjectionProbe } from './print/MapProjectionProbe';
import { usePrintFrame } from './print/usePrintFrame';
import { useCustomPrintSheet, useSheetFromCamera } from './print/useCustomPrintSheet';
import type { TeamMapViewport } from '../lib/mapTypes';
import { TeamMapView } from './TeamMapView';
import { useTeamMapPresentation } from './teamMapPresentation';
import { usePrintInsets } from './print/usePrintInsets';
import { PrintMapPadding } from './print/PrintMapPadding';
import { BrowserPrintNotice } from './print/BrowserPrintNotice';
import type { PrintProbeLookup } from '../lib/printProbe';

export interface TeamMapPrintViewProps {
  map: TeamMap;
  onClose: () => void;
  /** Identifies the print in the user's job list. */
  teamId?: string;
  teamName?: string;
  /**
   * A fresh Firebase ID token. Required for the server-rendered PDF: the print
   * session is exchanged from it server-side, because premium map data is
   * entitled per signed-in user.
   */
  getAuthToken?: () => Promise<string | null>;
  /**
   * The print server's probe for a point — which CRS a sheet centred there is
   * composed in and what prints there. Supplied by the host, because reaching
   * the Estate API is a domain concern and this library is presentation-only.
   */
  getPrintProbe?: PrintProbeLookup;
  /**
   * Pins and lines hidden in the Map tab's pin list. Left off this preview and
   * the sheet alike, so hiding stands in the list is how a team prints only the
   * ones in use; without it the print showed every pin the tab had hidden.
   */
  hiddenObjectIds?: ReadonlySet<string>;
}

/** Stable, so a host with no pin list neither allocates nor re-memoises. */
const NOTHING_HIDDEN: ReadonlySet<string> = new Set();

/**
 * Print the team map without entering the editor.
 *
 * Server-PDF-only (ratified product call, 2026-09-01): there is no browser
 * print of a raster capture to fall back to. Every ground is printable —
 * inside Finland, Sweden or France the server composes the sheet on that
 * country's national grid, and anywhere else on the worldwide raster tiles in
 * Web Mercator — so the only thing left to gate on is whether this surface can
 * produce a signed-in token and a centre to frame around.
 *
 * Full-screen rather than inline on the Map tab: the frame is the sheet's real
 * ground extent at the chosen scale, fitted to 88% of its container, so
 * composing the shot inside the tab's 460px-tall map left an A4 portrait frame
 * ~286px wide — narrower than the 276px preview panel that sits over it. The
 * full viewport gives the frame enough room to compose. Measured panel insets
 * reserve the unobscured map area when fitting the frame.
 *
 * The frame itself is the sheet the print server will compose: the printable
 * area times the scale, axis-aligned in the country's own projection around the
 * current centre, drawn as map geometry (`PrintFrameLayers`) rather than as a
 * DOM rectangle. It therefore follows pan and zoom truthfully, and the map is
 * fitted to it whenever the paper, orientation or scale changes.
 */
export function TeamMapPrintView({ map, onClose, teamId, teamName, getAuthToken, getPrintProbe, hiddenObjectIds = NOTHING_HIDDEN }: TeamMapPrintViewProps) {
  const { t } = useTranslation('teams');
  const surfaceRef = useRef<HTMLDivElement>(null);
  const padding = usePrintInsets(surfaceRef, true);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Paper, orientation, scale and the two custom controls — the same choice the
  // editor's print mode offers, so the rules live in one hook rather than in a
  // copy each (see `useCustomPrintSheet`).
  const sheet = useCustomPrintSheet();
  const { format, orientation, scale: pdfScale, page: customPage } = sheet;

  // What the map is currently showing. STATE, not a ref: the print frame is
  // derived from the centre, so a move has to re-render. It costs one render per
  // gesture rather than per frame — MapLibre reports on move end only.
  const [view, setView] = useState<TeamMapViewport | null>(null);
  const onViewChange = useCallback((v: TeamMapViewport) => { setView(v); }, []);

  // Layers + view settings, shared with the Map tab and its full-screen preview.
  // Anchored left here: the print panel permanently holds the right slot.
  const presentation = useTeamMapPresentation({ side: 'left' });
  const { overlayLayers } = presentation;

  // Where the ground is, for deciding whether the print server covers it. Taken
  // from the boundary rather than from the live view: the decision governs
  // whether the PDF controls exist at all, so it has to hold from mount — before
  // the map has reported a view — and it must not flip while the hunter pans
  // across a coverage edge, which is what deriving it from the live centre would
  // do. `groundCenterForPrint`
  // (mapPrintRegion.ts) is the same derivation the caller uses to decide
  // whether to offer this surface at all — shared so the two can never
  // disagree about where the ground is.
  const groundCenter = useMemo(() => groundCenterForPrint(map), [map]);

  // A signed-in token to exchange a print session with, and a centre to frame
  // the sheet around: the two conditions for a print that can actually succeed,
  // and this surface's ENTIRE precondition. The caller gates the button that
  // opens it the same way (see the doc comment above), so a false here past
  // that gate is the defensive case.
  const serverPrintEnabled = getAuthToken !== undefined && groundCenter !== null;

  const [omittedPinCount, setOmittedPinCount] = useState(0);
  const printJobs = useMapPrintJobs({ enabled: serverPrintEnabled, getAuthToken });

  // The print sheet the server will compose, plus the camera moves that keep
  // it in view — the same chain the editor's print mode runs (see
  // `usePrintFrame`). Always enabled: this surface exists only to print, so
  // there is no state of it in which the frame is not wanted.
  const { corners, projector, probe, printUnavailable, projectorPending, printError, retry, focus, projectRef, unprojectRef } =
    usePrintFrame({
      enabled: true,
      center: view?.center ?? null,
      zoom: view?.zoom ?? null,
      containerPx: view?.cssSize ?? null,
      padding,
      format,
      orientation,
      scale: pdfScale,
      page: customPage,
      sheetFollowsCamera: sheet.followsCamera,
      // The one sheet the camera cannot be made to match: below 100 mm the
      // page stops shrinking with the view, so the map zooms out to it.
      refitToken: sheet.zoomOutToken,
      getPrintProbe,
    });

  // The other direction: whichever custom control is active is recomputed from
  // what the map is showing. The frame hook is told not to fit back to it —
  // that pair of rules is what keeps the sheet and the camera from chasing each
  // other.
  useSheetFromCamera({
    sheet,
    enabled: true,
    center: view?.center ?? null,
    zoom: view?.zoom ?? null,
    containerPx: view?.cssSize ?? null,
    padding,
    projector,
    unprojectRef,
  });

  const printPinSizes = usePrintPinSizes(presentation.appearance);
  const artwork = usePrintArtwork(map, presentation.appearance, hiddenObjectIds, serverPrintEnabled);
  const printRequest = useMemo(() => {
    if (!projector || !corners || probe?.status !== 'SUCCESS' || printError || artwork.pending || presentation.cataloguePending || presentation.catalogueError) return null;
    // The live view's centre, so the sheet is centred on what the user framed.
    const center = view?.center ?? groundCenter;
    if (!center) return null;

    return buildMapPrintRequest({
      map,
      center,
      scale: pdfScale,
      format,
      orientation,
      symbolSvgs: artwork.svgs,
      symbolDesignSvgs: artwork.designSvgs,
      // The print panel's sliders, seeded from the Pin size setting.
      pinSizes: printPinSizes.sizes,
      // The whole object, not three scalars off it: the payload builder reads the
      // border treatment and every line-thickness term too, and a caller picking
      // fields by hand is how the printed sheet came to disagree with the preview it
      // was composed in. CLUB-667's hidden pin types ride along inside it.
      appearance: presentation.appearance,
      // The custom sheet's own millimetres, which win over `format` on the
      // wire's `page_width`/`page_height` — undefined while a named format is
      // selected.
      customPage,
      // The base map the surface behind this sheet is drawing. Without it the
      // payload always asked for the default terrain flavour, so a member who set
      // the map to aerial got an unshaded sheet back.
      baseMapId: presentation.baseMapId,
      // Printed when the preview shows them and the server draws them here.
      propertyBorders: printPropertyBorders(probe, presentation.enabledOverlayIds),
      // The sheet the frame on screen is drawn from, so the request is clipped
      // and simplified against exactly the rectangle the user composed — not a
      // second projector loaded here that could answer differently.
      sheet: projector,
      hiddenObjectIds,
      activeOverlays: presentation.activeOverlays,
      capabilities: probe.capabilities ?? [],
      printableFlavors: probe.flavors,
    });
  }, [map, view, groundCenter, pdfScale, format, orientation, customPage, presentation.appearance, printPinSizes.sizes, presentation.baseMapId, presentation.enabledOverlayIds, probe, projector, corners, printError, hiddenObjectIds, artwork.pending, artwork.svgs, artwork.designSvgs, presentation.activeOverlays, presentation.cataloguePending, presentation.catalogueError]);

  const handleCreatePdf = useCallback(async () => {
    const built = printRequest;
    if (built === null || built.pointCount > 1000000) return;
    // Reported, never swallowed: a print that quietly drops stands is worse than
    // one that says how many it could not draw.
    setOmittedPinCount(built.omittedPins.length);
    await printJobs.start({
      payload: built.request,
      scale: pdfScale,
      pageLabel: sheet.pageLabel,
      teamId,
      teamName,
    });
  }, [printRequest, printJobs, pdfScale, sheet.pageLabel, teamId, teamName]);
  useEffect(() => { closeRef.current?.focus(); }, []);

  // Escape dismisses an open layers/view-settings panel first, and only closes the
  // surface when none is open. Closing outright discarded the chosen paper size,
  // orientation and framing because the user reached for Escape to shut a panel.
  const { panelOpen, closePanels } = presentation;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (panelOpen) closePanels();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, panelOpen, closePanels]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('detail.map.printTitle')}
      // Black on screen, so the dimmed surround reads as "outside the crop", but
      // white on paper: if the transformed map ever leaves a sliver of the sheet
      // uncovered, that sliver should come out blank rather than as a band of ink.
      data-map-print="backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        ref={surfaceRef}
        className="wh-map-print-surface"
        data-map-print="canvas"
        style={{ position: 'relative', flex: 1, minHeight: 0 }}
      >
        <TeamMapView
          map={map}
          interactive={true}
          onViewChange={onViewChange}
          tileSource={presentation.tileSource}
          overlayLayers={overlayLayers}
          appearance={presentation.appearance}
          extraControls={presentation.controls}
          focus={focus}
          dimOutside={corners}
          // Pins and names at the size the sliders will print them, so moving a
          // slider shows on the map rather than only on the paper.
          printPinSizes={{ sizes: printPinSizes.sizes, scale: pdfScale }}
          hiddenObjectIds={hiddenObjectIds}
        >
          <PrintMapPadding padding={padding} />
          <PrintFrameLayers corners={corners} />
          <MapProjectionProbe projectRef={projectRef} unprojectRef={unprojectRef} />
        </TeamMapView>

        <button
          ref={closeRef}
          type="button"
          className="wh-btn-icon"
          data-map-print="hide"
          aria-label={t('detail.map.closePrint')}
          title={t('detail.common.close')}
          onClick={onClose}
          style={{
            position: 'absolute',
            // Top-LEFT, like the read-only full-screen preview: the interactive
            // map renders its zoom control stack at top-right, and a top-right
            // close button hides the '+' (CLUB-309).
            top: 16,
            left: 16,
            zIndex: 5,
            background: COLORS.surface,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          }}
        >
          <Icon name="close" size={24} />
        </button>

        {presentation.panels}

        {/* The two panels above anchor LEFT and sit at z-index 10 over a z-index 9
            backdrop, so they neither cover this panel (z-index 3, right) nor need
            dismissing before a print can be started. */}
        <BrowserPrintNotice />
        <PrintPreviewPanel
          topOffset={
            presentation.appearance.hiddenPinTypes.length > 0
              ? HIDDEN_PIN_TYPES_CHIP_STACK
              : 0
          }
          format={format}
          orientation={orientation}
          onFormatChange={sheet.onFormatChange}
          onOrientationChange={sheet.onOrientationChange}
          customPage={customPage}
          onCustomPageChange={sheet.onCustomPageChange}
          customPageActive={sheet.customPageActive}
          onActivateCustomPage={sheet.onActivateCustomPage}
          aspect={sheet.aspect}
          onAspectChange={sheet.onAspectChange}
          pinSizes={printPinSizes.sizes}
          pinSizeRange={printPinSizes.pinRange}
          onPinSizesChange={printPinSizes.onSizesChange}
          onClose={onClose}
          // MapPrintSection's "Create PDF" is the only way to print here; the
          // panel itself offers no print action.
          serverPrintSection={
            serverPrintEnabled ? (
              <MapPrintSection
                scale={pdfScale}
                onScaleChange={sheet.onScaleChange}
                format={format}
                orientation={orientation}
                // Without it a custom sheet's coverage, wait estimate and limit
                // check would all be computed for the named format behind it.
                page={customPage}
                customScale={sheet.customScale}
                onCustomScaleChange={sheet.onCustomScaleChange}
                customScaleActive={sheet.customScaleActive}
                onActivateCustomScale={sheet.onActivateCustomScale}
                jobs={printJobs.jobs}
                busy={printJobs.busy}
                // Create stays closed until the sheet's projector is in hand:
                // the request is clipped to the paper in that projector's
                // plane, so a click before it lands would post the whole
                // ground and hit the server's request-body cap instead of
                // printing the sheet on screen.
                sheetPending={projectorPending || !corners || artwork.pending || presentation.cataloguePending}
                sheetError={printError ?? (presentation.catalogueError ? 'catalogue' : null)}
                onRetrySheet={presentation.catalogueError ? presentation.retryCatalogue : retry}
                // A flavour the probe does not list for this centre would be
                // accepted and then fail; better to say so before the wait.
                // Where nothing prints at all, the panel says that instead.
                skips={printRequest?.skips}
                pointLimitExceeded={(printRequest?.pointCount ?? 0) > 1000000}
                printUnavailable={printUnavailable}
                error={printJobs.error}
                omittedPinCount={omittedPinCount}
                hiddenPinTypeCount={presentation.appearance.hiddenPinTypes.length}
                onCreate={handleCreatePdf}
                onOpen={(job) => {
                  // The result URL is self-authenticating and short-lived, so it is
                  // handed straight to the browser rather than stored anywhere — but
                  // only once it has been proven an https URL. `window.open` would
                  // execute a `javascript:` URL in this origin, and this string has
                  // crossed two services to get here.
                  const url = safePrintUrl(job.url);
                  if (url !== null) window.open(url, '_blank', 'noopener');
                }}
                onReprint={handleCreatePdf}
              />
            ) : (
              // Defensive only: the caller is expected to gate this surface's own
              // entry points on the same `serverPrintEnabled` condition (see the
              // component doc), so this renders only if that gate is bypassed —
              // a direct state change, a test, or a future caller.
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: COLORS.onSurfaceVariant }}>
                {t('detail.map.printUnavailable')}
              </p>
            )
          }
        />
      </div>
    </div>
  );
}
